import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  PartyPopper, ArrowRight, Upload, User,
  Building2, Users, Sparkles, MessageSquare,
  Video, Palette, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user, token, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  
  const [step, setStep] = useState(1);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Profile form state
  const [displayName, setDisplayName] = useState(user?.name || '');
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar_url || user?.avatar || null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Get invite token from URL or localStorage
  const inviteToken = searchParams.get('invite') || localStorage.getItem('pendingInviteToken');

  useEffect(() => {
    if (inviteToken) {
      fetchInviteInfo();
      // Store for persistence across page reloads
      localStorage.setItem('pendingInviteToken', inviteToken);
    } else {
      // No invite token, check if user has any accepted invitations
      checkRecentInvitations();
    }
  }, [inviteToken]);

  useEffect(() => {
    if (user?.name) {
      setDisplayName(user.name);
    }
    if (user?.avatar_url || user?.avatar) {
      setAvatarPreview(user.avatar_url || user.avatar);
    }
  }, [user]);

  const fetchInviteInfo = async () => {
    try {
      const response = await fetch(`${API_URL}/invitations/by-token/${inviteToken}`);
      if (response.ok) {
        const data = await response.json();
        setInviteInfo(data);
      }
    } catch (err) {
      console.error('Error fetching invite info:', err);
    } finally {
      setLoading(false);
    }
  };

  const checkRecentInvitations = async () => {
    // If no invite token, try to get info from pending invitations
    try {
      const response = await fetch(`${API_URL}/invitations/pending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const invites = await response.json();
        if (invites.length > 0) {
          // Use the most recent invite
          setInviteInfo(invites[0]);
        }
      }
    } catch (err) {
      console.error('Error checking invitations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB. Please choose a smaller image.');
      return;
    }

    // Validate minimum size (optional - at least 100x100)
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      
      if (img.width < 100 || img.height < 100) {
        toast.warning('Image is quite small. For best results, use an image at least 200x200 pixels.');
      }
      
      setAvatarFile(file);
      
      // Preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
        toast.success('Photo selected! Click "Complete Setup" to save.');
      };
      reader.readAsDataURL(file);
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      toast.error('Invalid image file. Please try another image.');
    };
    
    img.src = objectUrl;
  };

  const handleSaveProfile = async () => {
    if (!displayName.trim()) {
      toast.error('Please enter a display name');
      return;
    }

    if (!token) {
      toast.error('Authentication required. Please log in again.');
      return;
    }

    setSaving(true);
    try {
      // Update name if changed
      if (displayName !== user?.name) {
        const nameResponse = await fetch(`${API_URL}/users/profile`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ name: displayName })
        });
        
        if (!nameResponse.ok) {
          const errorData = await nameResponse.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Failed to update name');
        }
      }

      // Upload avatar if selected
      if (avatarFile) {
        setUploadingAvatar(true);
        setUploadProgress(10);
        
        // Simulate progress for better UX
        const progressInterval = setInterval(() => {
          setUploadProgress(prev => {
            if (prev >= 90) {
              clearInterval(progressInterval);
              return 90;
            }
            return prev + 10;
          });
        }, 100);
        
        try {
          const formData = new FormData();
          formData.append('file', avatarFile);
          
          const avatarResponse = await fetch(`${API_URL}/users/profile/avatar`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData
          });
          
          clearInterval(progressInterval);
          setUploadProgress(100);
          
          if (!avatarResponse.ok) {
            const errorData = await avatarResponse.json().catch(() => ({}));
            throw new Error(errorData.detail || 'Failed to upload avatar');
          }

          const avatarData = await avatarResponse.json();
          if (avatarData.avatar_url) {
            setAvatarPreview(avatarData.avatar_url);
          }
        } catch (error) {
          clearInterval(progressInterval);
          setUploadProgress(0);
          throw error;
        } finally {
          setTimeout(() => {
            setUploadingAvatar(false);
            setUploadProgress(0);
          }, 300);
        }
      }

      // Refresh user data
      if (refreshUser) {
        await refreshUser();
      }

      // Clear the pending invite token
      localStorage.removeItem('pendingInviteToken');
      
      // Mark onboarding as complete
      localStorage.setItem('onboardingComplete', 'true');

      toast.success('Profile updated successfully! 🎉', {
        description: 'Welcome to BoardChat!',
        duration: 2000,
      });
      
      // Small delay to show success message and allow user to see the success
      setTimeout(() => {
        navigate('/dashboard');
      }, 1000);
    } catch (err) {
      console.error('Error saving profile:', err);
      toast.error(err.message || 'Failed to save profile. Please try again.');
      setUploadingAvatar(false);
      setUploadProgress(0);
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    localStorage.removeItem('pendingInviteToken');
    localStorage.setItem('onboardingComplete', 'true');
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Progress Indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className={`w-3 h-3 rounded-full transition-colors ${step >= 1 ? 'bg-blue-500' : 'bg-white/20'}`} />
          <div className={`w-12 h-0.5 transition-colors ${step >= 2 ? 'bg-blue-500' : 'bg-white/20'}`} />
          <div className={`w-3 h-3 rounded-full transition-colors ${step >= 2 ? 'bg-blue-500' : 'bg-white/20'}`} />
        </div>

        {/* Step 1: Welcome */}
        {step === 1 && (
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 text-center" data-testid="onboarding-welcome">
            {/* Celebration Icon */}
            <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
              <PartyPopper className="w-10 h-10 text-white" />
            </div>

            <h1 className="text-3xl font-bold text-white mb-2">
              Welcome to BoardChat! 🎉
            </h1>
            
            {inviteInfo ? (
              <div className="mb-6">
                <p className="text-white/70 text-lg mb-4">
                  <span className="text-white font-medium">{inviteInfo.inviter_name}</span> invited you to join
                </p>
                
                {/* Workspace/Hub Card */}
                <div className="bg-white/10 rounded-xl p-4 inline-flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    inviteInfo.target_type === 'hub' ? 'bg-purple-500' : 'bg-blue-500'
                  }`}>
                    {inviteInfo.target_type === 'hub' ? (
                      <Users className="w-6 h-6 text-white" />
                    ) : (
                      <Building2 className="w-6 h-6 text-white" />
                    )}
                  </div>
                  <div className="text-left">
                    <p className="text-white font-semibold">{inviteInfo.target_name}</p>
                    <p className="text-white/60 text-sm capitalize">{inviteInfo.target_type} • {inviteInfo.role}</p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-white/70 text-lg mb-6">
                Your all-in-one team collaboration platform
              </p>
            )}

            {/* Features Preview */}
            <div className="grid grid-cols-2 gap-3 mb-8">
              <div className="bg-white/5 rounded-lg p-3 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-blue-400" />
                <span className="text-white/80 text-sm">Team Chat</span>
              </div>
              <div className="bg-white/5 rounded-lg p-3 flex items-center gap-2">
                <Video className="w-5 h-5 text-green-400" />
                <span className="text-white/80 text-sm">Video Calls</span>
              </div>
              <div className="bg-white/5 rounded-lg p-3 flex items-center gap-2">
                <Palette className="w-5 h-5 text-purple-400" />
                <span className="text-white/80 text-sm">Whiteboards</span>
              </div>
              <div className="bg-white/5 rounded-lg p-3 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-yellow-400" />
                <span className="text-white/80 text-sm">Kanban Boards</span>
              </div>
            </div>

            <Button
              onClick={() => setStep(2)}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 text-lg"
              data-testid="onboarding-next-btn"
            >
              Let's Set Up Your Profile
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        )}

        {/* Step 2: Profile Setup */}
        {step === 2 && (
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 animate-in fade-in slide-in-from-bottom-4 duration-300" data-testid="onboarding-profile">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-white mb-2">Set Up Your Profile</h2>
              <p className="text-white/60">Help your team recognize you</p>
            </div>

            {/* Avatar Upload */}
            <div className="flex flex-col items-center mb-6">
              <div className="relative group">
                {avatarPreview && avatarPreview.startsWith('data:') ? (
                  <img
                    src={avatarPreview}
                    alt="Avatar preview"
                    className="w-24 h-24 rounded-full object-cover border-4 border-white/20 shadow-lg"
                  />
                ) : avatarPreview ? (
                  <img
                    src={avatarPreview}
                    alt="Avatar preview"
                    className="w-24 h-24 rounded-full object-cover border-4 border-white/20 shadow-lg"
                  />
                ) : (
                  <div 
                    className="w-24 h-24 rounded-full flex items-center justify-center border-4 border-white/20 shadow-lg"
                    style={{ backgroundColor: user?.avatar_color || '#6366F1' }}
                  >
                    <span className="text-white text-3xl font-bold">
                      {displayName?.charAt(0)?.toUpperCase() || user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  </div>
                )}
                
                {/* Upload overlay */}
                <label className={`absolute inset-0 flex items-center justify-center bg-black/60 rounded-full cursor-pointer transition-all ${
                  uploadingAvatar ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                }`}>
                  {uploadingAvatar ? (
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span className="text-white text-xs">{uploadProgress}%</span>
                    </div>
                  ) : (
                    <Upload className="w-6 h-6 text-white" />
                  )}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleAvatarChange}
                    className="hidden"
                    disabled={uploadingAvatar || saving}
                    data-testid="avatar-upload-input"
                  />
                </label>
                
                {/* Upload progress ring */}
                {uploadingAvatar && (
                  <svg className="absolute inset-0 w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
                    <circle
                      cx="50"
                      cy="50"
                      r="45"
                      fill="none"
                      stroke="rgba(255,255,255,0.3)"
                      strokeWidth="4"
                    />
                    <circle
                      cx="50"
                      cy="50"
                      r="45"
                      fill="none"
                      stroke="white"
                      strokeWidth="4"
                      strokeDasharray={`${2 * Math.PI * 45}`}
                      strokeDashoffset={`${2 * Math.PI * 45 * (1 - uploadProgress / 100)}`}
                      strokeLinecap="round"
                      className="transition-all duration-300"
                    />
                  </svg>
                )}
              </div>
              <p className="text-white/50 text-sm mt-2">
                {uploadingAvatar ? 'Uploading...' : avatarFile ? 'Click to change photo' : 'Click to upload photo'}
              </p>
              {avatarFile && (
                <p className="text-white/40 text-xs mt-1">
                  {(avatarFile.size / 1024).toFixed(0)} KB
                </p>
              )}
            </div>

            {/* Name Input */}
            <div className="space-y-2 mb-6">
              <Label htmlFor="displayName" className="text-white/80">
                Display Name <span className="text-red-400">*</span>
              </Label>
              <Input
                id="displayName"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="How should we call you?"
                className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20"
                data-testid="display-name-input"
                maxLength={50}
                autoFocus
              />
              {displayName.trim() && (
                <p className="text-white/40 text-xs">
                  {displayName.trim().length}/50 characters
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                onClick={handleSaveProfile}
                disabled={!displayName.trim() || saving || uploadingAvatar}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                data-testid="save-profile-btn"
              >
                {saving || uploadingAvatar ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {uploadingAvatar ? 'Uploading photo...' : 'Saving...'}
                  </div>
                ) : (
                  <>
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                    Complete Setup
                  </>
                )}
              </Button>
              
              <Button
                variant="ghost"
                onClick={handleSkip}
                disabled={saving || uploadingAvatar}
                className="w-full text-white/60 hover:text-white hover:bg-white/10 disabled:opacity-50"
                data-testid="skip-onboarding-btn"
              >
                Skip for now
              </Button>
            </div>
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-white/40 text-sm mt-6">
          Step {step} of 2
        </p>
      </div>
    </div>
  );
};

export default OnboardingPage;
